import makeSlug from "@/utils/makeSlug";
import { useMarket } from "@app/_context";
import { useAppForm } from "@features/forms/config";
import { registrationInput } from "@repo/core/account";
import { FirebaseError } from "firebase/app";
import { useRouter } from "next/navigation";
import { z } from "zod";
import { mapValidationError } from "../services/mapValidationError";
import normalizePhoneNumber from "../utils/normalizePhoneNumber";
import useRegisterAccount from "./useRegisterAccount";
import useSignIn from "./useSignIn";

const input = registrationInput
  .extend({ repeatPassword: z.string() })
  .superRefine((val, ctx) => {
    if (val.password !== val.repeatPassword) {
      ctx.addIssue({
        code: "custom",
        message: "errorPasswordsDoNotMatch",
        path: ["repeatPassword"],
      });
    }

    if (val.isCompany) {
      if (!val.companyName?.trim()) {
        ctx.addIssue({
          code: "custom",
          message: "errorMissingFieldValue",
          path: ["companyName"],
        });
      }
      if (!val.companyOrganizationalNumber?.trim()) {
        ctx.addIssue({
          code: "custom",
          message: "errorMissingFieldValue",
          path: ["companyOrganizationalNumber"],
        });
      }
    }
  });

export default function useRegisterAccountForm() {
  const router = useRouter();
  const { market } = useMarket().state;
  const { registerAccountAsync } = useRegisterAccount();
  const signIn = useSignIn();

  return useAppForm({
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      repeatPassword: "",
      phoneNumber: "",
      acceptsEmail: true,
      acceptsSms: true,
      isCompany: false,
      locale: market.serviceLocale,
      socialSecurityNumber: "",
      companyName: "",
      companyOrganizationalNumber: "",
      birthDate: "",
      street: "",
      city: "",
      zipCode: "",
    },
    validators: {
      onChange: (payload) => {
        const validation = input.safeParse(payload.value);
        if (!validation.success) {
          return {
            fields: Object.fromEntries(
              validation.error.issues.map((issue) => [
                issue.path.join("."),
                issue.message,
              ]),
            ),
          };
        }
        return undefined;
      },
      onSubmitAsync: async ({ value }) => {
        const { repeatPassword: _, ...registration } = value;
        registration.phoneNumber = normalizePhoneNumber(
          registration.phoneNumber,
          market.code,
        );

        let accountCreated = false;

        try {
          const result = await registerAccountAsync(registration);

          const validationErrors = result.validationErrors ?? [];

          if (validationErrors.length > 0) {
            const fields = validationErrors
              .map((validationError) =>
                mapValidationError<string>(validationError),
              )
              .filter((field) => field !== undefined);
            if (fields.length > 0) {
              return {
                fields: Object.fromEntries(fields),
              };
            }
            return "internalError";
          }

          if (result.success === false) {
            return "internalError";
          }

          accountCreated = true;

          await signIn({
            strategy: "email",
            email: registration.email,
            password: registration.password,
          });
        } catch (error) {
          if (accountCreated) {
            // Account exists — guide the user to sign in instead of re-registering.
            return "errorRegistrationSignInFailed";
          }

          if (error instanceof FirebaseError) {
            const { AuthErrorCodes } = await import("firebase/auth");
            if (error.code === AuthErrorCodes.EMAIL_EXISTS) {
              return { fields: { email: "errorEmailAlreadyTaken" } };
            }
          }

          return "internalError";
        }

        router.push(makeSlug(market.locale, "/account/orders"));

        return undefined;
      },
    },
  });
}
